import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Shield, CheckCircle, XCircle, Clock, User, Mail, Phone,
  FileText, Eye, Download, AlertTriangle, Search, Filter,
  ChevronDown, X, Calendar, Globe
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import api from '../../lib/api';

export default function AdminKYC() {
  const [pendingKyc, setPendingKyc] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    fetchKycData();
  }, []);

  const fetchKycData = async () => {
    try {
      const [pendingRes, usersRes] = await Promise.all([
        api.get('/admin/kyc/pending'),
        api.get('/admin/users')
      ]);
      setPendingKyc(pendingRes.data.pending_kyc || []);
      setAllUsers(usersRes.data.users || []);
    } catch (error) {
      toast.error('Failed to load KYC data');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (userId) => {
    setProcessing(true);
    try {
      await api.post(`/admin/kyc/${userId}/approve`);
      toast.success('KYC approved successfully');
      setShowModal(false);
      setSelectedUser(null);
      fetchKycData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve KYC');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async (userId) => {
    if (!rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }
    setProcessing(true);
    try {
      await api.post(`/admin/kyc/${userId}/reject?reason=${encodeURIComponent(rejectionReason)}`);
      toast.success('KYC rejected');
      setShowModal(false);
      setSelectedUser(null);
      setRejectionReason('');
      fetchKycData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject KYC');
    } finally {
      setProcessing(false);
    }
  };

  const openReviewModal = (user) => {
    setSelectedUser(user);
    setShowModal(true);
    setRejectionReason('');
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-green-500/20 text-green-400 text-xs font-medium">
            <CheckCircle className="w-3 h-3" /> Approved
          </span>
        );
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-500/20 text-amber-400 text-xs font-medium">
            <Clock className="w-3 h-3" /> Pending
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-red-500/20 text-red-400 text-xs font-medium">
            <XCircle className="w-3 h-3" /> Rejected
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-zinc-500/20 text-zinc-400 text-xs font-medium">
            <AlertTriangle className="w-3 h-3" /> Not Submitted
          </span>
        );
    }
  };

  const filteredUsers = allUsers.filter(user => {
    const matchesSearch = user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          user.full_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterStatus === 'all' || user.kyc_status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-amber-500/20 border-t-amber-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-kyc-page">
      {/* Header Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }}
          className="glass-card p-4 border-l-4 border-amber-500"
        >
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-4 h-4 text-amber-400" />
            <p className="text-xs text-zinc-500">Pending Review</p>
          </div>
          <p className="text-2xl font-bold text-amber-400">{pendingKyc.length}</p>
        </motion.div>
        
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="glass-card p-4 border-l-4 border-green-500"
        >
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle className="w-4 h-4 text-green-400" />
            <p className="text-xs text-zinc-500">Approved</p>
          </div>
          <p className="text-2xl font-bold text-green-400">
            {allUsers.filter(u => u.kyc_status === 'approved').length}
          </p>
        </motion.div>
        
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="glass-card p-4 border-l-4 border-red-500"
        >
          <div className="flex items-center gap-2 mb-2">
            <XCircle className="w-4 h-4 text-red-400" />
            <p className="text-xs text-zinc-500">Rejected</p>
          </div>
          <p className="text-2xl font-bold text-red-400">
            {allUsers.filter(u => u.kyc_status === 'rejected').length}
          </p>
        </motion.div>
        
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="glass-card p-4 border-l-4 border-zinc-500"
        >
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-zinc-400" />
            <p className="text-xs text-zinc-500">Not Submitted</p>
          </div>
          <p className="text-2xl font-bold text-zinc-400">
            {allUsers.filter(u => !u.kyc_status || u.kyc_status === 'not_submitted').length}
          </p>
        </motion.div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-white/10 pb-2">
        <button
          onClick={() => setActiveTab('pending')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
            activeTab === 'pending' 
              ? 'bg-amber-500/20 text-amber-400' 
              : 'text-zinc-400 hover:text-white'
          }`}
        >
          <Clock className="w-4 h-4" />
          Pending Review ({pendingKyc.length})
        </button>
        <button
          onClick={() => setActiveTab('all')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
            activeTab === 'all' 
              ? 'bg-amber-500/20 text-amber-400' 
              : 'text-zinc-400 hover:text-white'
          }`}
        >
          <User className="w-4 h-4" />
          All Users
        </button>
      </div>

      {/* Pending KYC Tab */}
      {activeTab === 'pending' && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="space-y-4"
        >
          {pendingKyc.length === 0 ? (
            <div className="glass-card p-8 text-center">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
              <p className="text-zinc-400">No pending KYC reviews</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {pendingKyc.map((user, idx) => (
                <motion.div
                  key={user.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  className="glass-card p-4 border border-amber-500/20"
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center text-amber-400 font-bold">
                        {user.full_name?.charAt(0)?.toUpperCase() || 'U'}
                      </div>
                      <div>
                        <h3 className="text-white font-medium">{user.full_name}</h3>
                        <p className="text-xs text-zinc-500">{user.email}</p>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {getStatusBadge('pending')}
                          <span className="text-xs text-zinc-500">
                            {user.kyc_document_type?.replace('_', ' ').toUpperCase()}
                          </span>
                          <span className="text-xs text-zinc-500">
                            Submitted: {user.kyc_submitted_at ? new Date(user.kyc_submitted_at).toLocaleDateString() : 'N/A'}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        onClick={() => openReviewModal(user)}
                        className="bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 border border-amber-500/30"
                        data-testid={`review-kyc-${user.id}`}
                      >
                        <Eye className="w-4 h-4 mr-2" /> Review
                      </Button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
      )}

      {/* All Users Tab */}
      {activeTab === 'all' && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="space-y-4"
        >
          {/* Search and Filter */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <input
                type="text"
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-amber-500/50"
              />
            </div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-amber-500/50"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="not_submitted">Not Submitted</option>
            </select>
          </div>

          {/* Users Table */}
          <div className="glass-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="data-table w-full">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Email</th>
                    <th>Document Type</th>
                    <th>Submitted</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user) => (
                    <tr key={user.id}>
                      <td>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center text-amber-400 text-xs font-bold">
                            {user.full_name?.charAt(0)?.toUpperCase() || 'U'}
                          </div>
                          <span className="text-white">{user.full_name}</span>
                        </div>
                      </td>
                      <td className="text-zinc-400 text-sm">{user.email}</td>
                      <td className="text-zinc-400 text-sm">
                        {user.kyc_document_type?.replace('_', ' ').toUpperCase() || '-'}
                      </td>
                      <td className="text-zinc-500 text-sm">
                        {user.kyc_submitted_at ? new Date(user.kyc_submitted_at).toLocaleDateString() : '-'}
                      </td>
                      <td>{getStatusBadge(user.kyc_status)}</td>
                      <td>
                        {user.kyc_status === 'pending' && (
                          <Button 
                            size="sm"
                            onClick={() => openReviewModal(user)}
                            className="bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 text-xs"
                          >
                            Review
                          </Button>
                        )}
                        {user.kyc_status === 'approved' && (
                          <span className="text-green-400 text-xs">Verified</span>
                        )}
                        {user.kyc_status === 'rejected' && (
                          <span className="text-red-400 text-xs">Rejected</span>
                        )}
                        {(!user.kyc_status || user.kyc_status === 'not_submitted') && (
                          <span className="text-zinc-500 text-xs">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </motion.div>
      )}

      {/* Review Modal */}
      <AnimatePresence>
        {showModal && selectedUser && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-zinc-900 rounded-2xl p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-white/10"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-full bg-amber-500 flex items-center justify-center text-black text-xl font-bold">
                    {selectedUser.full_name?.charAt(0)?.toUpperCase() || 'U'}
                  </div>
                  <div>
                    <h2 className="text-xl text-white font-medium">{selectedUser.full_name}</h2>
                    <p className="text-sm text-zinc-500">{selectedUser.email}</p>
                  </div>
                </div>
                <button 
                  onClick={() => setShowModal(false)}
                  className="p-2 rounded-lg hover:bg-white/5 text-zinc-400 hover:text-white transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* User Info */}
              <div className="grid sm:grid-cols-2 gap-4 mb-6">
                <div className="glass p-4 rounded-lg">
                  <div className="flex items-center gap-2 mb-2 text-zinc-500">
                    <Mail className="w-4 h-4" />
                    <span className="text-xs">Email</span>
                  </div>
                  <p className="text-white text-sm">{selectedUser.email}</p>
                </div>
                <div className="glass p-4 rounded-lg">
                  <div className="flex items-center gap-2 mb-2 text-zinc-500">
                    <Phone className="w-4 h-4" />
                    <span className="text-xs">Phone</span>
                  </div>
                  <p className="text-white text-sm">{selectedUser.phone || 'N/A'}</p>
                </div>
                <div className="glass p-4 rounded-lg">
                  <div className="flex items-center gap-2 mb-2 text-zinc-500">
                    <FileText className="w-4 h-4" />
                    <span className="text-xs">Document Type</span>
                  </div>
                  <p className="text-white text-sm">{selectedUser.kyc_document_type?.replace('_', ' ').toUpperCase() || 'N/A'}</p>
                </div>
                <div className="glass p-4 rounded-lg">
                  <div className="flex items-center gap-2 mb-2 text-zinc-500">
                    <Calendar className="w-4 h-4" />
                    <span className="text-xs">Submitted At</span>
                  </div>
                  <p className="text-white text-sm">
                    {selectedUser.kyc_submitted_at ? new Date(selectedUser.kyc_submitted_at).toLocaleString() : 'N/A'}
                  </p>
                </div>
              </div>

              {/* Document Images */}
              <div className="mb-6">
                <h3 className="text-white font-medium mb-4">Submitted Documents</h3>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {selectedUser.kyc_document_front && (
                    <div>
                      <p className="text-xs text-zinc-500 mb-2">Document Front</p>
                      <div className="relative group">
                        <img 
                          src={selectedUser.kyc_document_front} 
                          alt="Document Front" 
                          className="w-full h-48 object-cover rounded-lg border border-white/10"
                        />
                        <a 
                          href={selectedUser.kyc_document_front} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-lg"
                        >
                          <Eye className="w-6 h-6 text-white" />
                        </a>
                      </div>
                    </div>
                  )}
                  {selectedUser.kyc_document_back && (
                    <div>
                      <p className="text-xs text-zinc-500 mb-2">Document Back</p>
                      <div className="relative group">
                        <img 
                          src={selectedUser.kyc_document_back} 
                          alt="Document Back" 
                          className="w-full h-48 object-cover rounded-lg border border-white/10"
                        />
                        <a 
                          href={selectedUser.kyc_document_back} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-lg"
                        >
                          <Eye className="w-6 h-6 text-white" />
                        </a>
                      </div>
                    </div>
                  )}
                  {selectedUser.kyc_selfie && (
                    <div>
                      <p className="text-xs text-zinc-500 mb-2">Selfie with Document</p>
                      <div className="relative group">
                        <img 
                          src={selectedUser.kyc_selfie} 
                          alt="Selfie" 
                          className="w-full h-48 object-cover rounded-lg border border-white/10"
                        />
                        <a 
                          href={selectedUser.kyc_selfie} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center rounded-lg"
                        >
                          <Eye className="w-6 h-6 text-white" />
                        </a>
                      </div>
                    </div>
                  )}
                </div>
                {!selectedUser.kyc_document_front && !selectedUser.kyc_selfie && (
                  <div className="text-center py-8 text-zinc-500">
                    <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No documents uploaded</p>
                  </div>
                )}
              </div>

              {/* Rejection Reason */}
              <div className="mb-6">
                <label className="text-xs text-zinc-500 mb-2 block">Rejection Reason (required for rejection)</label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Enter reason for rejection..."
                  className="w-full p-3 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-red-500/50 resize-none"
                  rows={3}
                />
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={() => handleApprove(selectedUser.id)}
                  disabled={processing}
                  className="flex-1 bg-green-500 hover:bg-green-600 text-white"
                  data-testid="approve-kyc-btn"
                >
                  {processing ? (
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                  ) : (
                    <CheckCircle className="w-4 h-4 mr-2" />
                  )}
                  Approve KYC
                </Button>
                <Button
                  onClick={() => handleReject(selectedUser.id)}
                  disabled={processing || !rejectionReason.trim()}
                  className="flex-1 bg-red-500 hover:bg-red-600 text-white disabled:opacity-50"
                  data-testid="reject-kyc-btn"
                >
                  {processing ? (
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                  ) : (
                    <XCircle className="w-4 h-4 mr-2" />
                  )}
                  Reject KYC
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
